import { useState, useEffect } from 'react';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

const EMPTY = {
  totalAssets: 0,
  assetsTrend: null,
  activeTasks: 0,
  monthlyAssetSeries: [],
  taskTrendSeries: [],
  taskStatusCounts: { assigned: 0, ongoing: 0, completed: 0 },
  onboarding: {
    totalTasks: 0,
    completedTasks: 0,
    totalAssets: 0,
    totalWorkflows: 0,
    totalUsers: 0,
    latestCompletedTaskAt: null,
  },
};

export default function useAdminSummary() {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const res = await fetchWithAuth(
          `${API_URL}/v1/admin/summary`,
          { cache: 'no-store' }
        );

        // Any non-OK or empty response falls back to EMPTY
        if (!res.ok) throw new Error('network');

        const raw = await res.json();
        if (!raw) throw new Error('empty');

        if (isMounted) setData(transform(raw));
      } catch {
        if (isMounted) setData(EMPTY); // silent fallback
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  return { data, loading };
}

/* backend → UI mapping */
function transform(raw) {
  try {
    return {
      totalAssets: raw?.assets?.total ?? 0,
      // Not `?? 0`: the backend sends null when growth from an empty previous
      // month has no percentage, and StatCard drops the trend line only for a
      // non-number. Coercing to 0 turned that into a flat "0% from last month".
      assetsTrend: raw?.assets?.monthlyChange ?? null,
      activeTasks: raw?.tasks?.active ?? 0,
      monthlyAssetSeries: raw?.assets?.monthlySeries ?? [],
      taskTrendSeries: raw?.tasks?.completionSeries ?? [],
      taskStatusCounts: raw?.tasks?.byStatus ?? { assigned: 0, ongoing: 0, completed: 0 },
      onboarding: {
        totalTasks: raw?.onboarding?.totalTasks ?? 0,
        completedTasks: raw?.onboarding?.completedTasks ?? 0,
        totalAssets: raw?.onboarding?.totalAssets ?? 0,
        totalWorkflows: raw?.onboarding?.totalWorkflows ?? 0,
        totalUsers: raw?.onboarding?.totalUsers ?? 0,
        latestCompletedTaskAt: raw?.onboarding?.latestCompletedTaskAt ?? null,
      },
    };
  } catch {
    /* unexpected shape → safe zeros */
    return EMPTY;
  }
}
